import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'reactstrap';
import { ProductImage, ProductName } from '@bottomless/common/components';

const productMeta = product => [
  { label: 'Origin', value: product.origin?.name },
  { label: 'Roast', value: product.roast?.name },
];

const FirstProductComponent = ({ product, variant, onClick, onStart, personalized, grind, active, isLoading }) => {
  const handleStartClick = useCallback(
    e => {
      e.stopPropagation();
      return onStart({ product, variant });
    },
    [onStart, product, variant]
  );

  const notes = (product.tasting_notes || []).slice(0, 3);
  const details = [
    ...productMeta(product),
    { label: 'Bag', value: variant?.size ? `${variant.size} oz` : undefined },
  ].filter(({ value }) => value);

  return (
    <Card
      key={product._id}
      body
      outline
      color={active ? 'primary' : undefined}
      className="quiz-summary-product-card cursor-pointer"
      onClick={onClick}
    >
      <div className="quiz-summary-product-image">
        <ProductImage product={product} width={180} />
      </div>
      <div className="quiz-summary-product-body">
        <div className="text-uppercase quiz-summary-product-vendor">{product.vendor_name}</div>
        <div className="quiz-summary-product-name">
          <ProductName product={product} personalized={personalized} grind={grind} />
        </div>
        {!!notes.length && (
          <div className="quiz-summary-product-notes">
            {notes.map(note => (
              <span key={note._id || note.name}>{note.name}</span>
            ))}
          </div>
        )}
        <div className="quiz-summary-product-meta">
          {details.map(({ label, value }) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <Button
          color="primary"
          block
          className="quiz-summary-start-button"
          disabled={isLoading}
          onClick={handleStartClick}
        >
          {isLoading ? 'Processing...' : 'Start Here'}
        </Button>
      </div>
    </Card>
  );
};

FirstProductComponent.propTypes = {
  rotationPrice: PropTypes.number,
  variant: PropTypes.shape({
    _id: PropTypes.string,
    size: PropTypes.number,
  }),
  onClick: PropTypes.func,
  onStart: PropTypes.func.isRequired,
  product: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    vendor_name: PropTypes.string.isRequired,
    small_image_src: PropTypes.string.isRequired,
    origin: PropTypes.shape({
      name: PropTypes.string,
    }),
    roast: PropTypes.shape({
      name: PropTypes.string,
    }),
    tasting_notes: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string.isRequired,
      })
    ),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        size: PropTypes.number.isRequired,
      })
    ).isRequired,
  }),
  pricingRule: PropTypes.object,
  isFirstBag: PropTypes.bool,
  personalized: PropTypes.object,
  grind: PropTypes.string,
  dynamicPricing: PropTypes.shape({
    price: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
  }),
  active: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export const FirstProduct = memo(FirstProductComponent);
